// Routing configuration building blocks (RF-CFG-05/06/08/12/14, RF-MAESTRO-01/08,
// RF-GL-01..03): the global draft editor (roles, Maestro + strong substitutes, free-first,
// budgets, Gauntlet), the version list (draft → validate → activate, rollback) and the
// effective table agent → role → model/API → tools → pool → fallback → reviewer/Maestro.
import { useState, type ReactNode } from "react";
import { DEFAULT_CODEBASE_ANALYSIS, DEFAULT_ROUTING_INTELLIGENCE, type AgentRole } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { activateRoutingConfig, validateRoutingConfig } from "../../api/endpoints-wave2.ts";
import type { AgentProfile, EffectiveConfigView, EffectiveRow, ModelTarget, RoutingConfigBody, RoutingConfigLayer, RoutingConfigRecord } from "../../api/types-wave2.ts";
import { Button } from "../../components/Button.tsx";
import { SelectField, TextField } from "../../components/Field.tsx";
import { Pill } from "../../components/Status.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { CAPABILITY_KEYS, FormSection, OriginBadge, ROLE_IDS, StateBadge } from "./shared.tsx";
import { RI_TIER_MAX, tierOptions, toggleTierId } from "./modelKinds.ts";

export const FALLBACK_ERRORS = ["rate_limited", "credit_exhausted", "unavailable", "model_not_found", "context_exceeded"] as const;
export const ESCALATION_TRIGGERS = ["capability_missing", "irreducible_context", "reproducible_quality_failure", "risk_class", "material_stagnation"] as const;
export const GAUNTLET_REQUIRED = ["contract_change", "data_migration", "authentication", "high_risk"] as const;

type RoleRoute = NonNullable<RoutingConfigBody["roles"][AgentRole]>;

/**
 * The editor's body: every section complete, except `assistant`, kept only as stored — its defaults apply at resolution
 * (domain STATIC_FIELDS), so saving a layer never writes them into it (final review minor).
 */
export type EditableRoutingBody = Omit<RoutingConfigBody, "assistant"> & Pick<RoutingConfigLayer, "assistant">;

export function defaultBody(): EditableRoutingBody {
  return {
    roles: {},
    maestro: { model_target_id: null, substitute_model_target_ids: [] },
    free_first: { enabled: true, escalation_triggers: [] },
    budgets: { soft_limit_cents: null, hard_limit_cents: null, window: "month" },
    agents: { runtime: "legacy", selection: "profile" },
    gauntlet: { mode: "conventional", required_for: [], max_rounds: 4, stagnation_rounds: 2 },
    // D24: every Routing Intelligence flag is off by default (switching them on is the owner's decision).
    routing_intelligence: DEFAULT_ROUTING_INTELLIGENCE,
    // Onboarding: codebase analysis is off by default (turning it on is the organization admin's decision).
    codebase_analysis: DEFAULT_CODEBASE_ANALYSIS,
  };
}

/** Completes a stored (partial) body with defaults so the editor always has every section. */
export function completeBody(body: RoutingConfigLayer | undefined): EditableRoutingBody {
  const d = defaultBody();
  return {
    roles: { ...(body?.roles ?? {}) },
    maestro: { ...d.maestro, ...(body?.maestro ?? {}) },
    free_first: { ...d.free_first, ...(body?.free_first ?? {}) },
    budgets: { ...d.budgets, ...(body?.budgets ?? {}) },
    agents: { ...d.agents, ...(body?.agents ?? {}) },
    gauntlet: { ...d.gauntlet, ...(body?.gauntlet ?? {}) },
    // Kept as stored: the editor never drops the Routing Intelligence section when it saves a new draft.
    routing_intelligence: { ...d.routing_intelligence, ...(body?.routing_intelligence ?? {}) },
    // Kept as stored, like the Routing Intelligence section: saving a new draft never drops codebase_analysis.
    codebase_analysis: { ...d.codebase_analysis, ...(body?.codebase_analysis ?? {}) },
    ...(body?.assistant ? { assistant: body.assistant } : {}),
  };
}

function targetName(targets: ModelTarget[], id: string | null | undefined): string {
  if (!id) return "—";
  const x = targets.find((m) => m.id === id);
  return x ? `${x.display_name} (${x.external_model_id})` : id;
}

function numOrNull(raw: string): number | null {
  const n = Number(raw);
  return raw.trim() === "" || !Number.isFinite(n) ? null : n;
}

/** Global draft editor. Saving always creates a new draft version; nothing is activated here. */
export function RoutingEditor({
  initial,
  profiles,
  targets,
  connections = [],
  busy,
  onSave,
  onCancel,
}: {
  initial: EditableRoutingBody;
  profiles: AgentProfile[];
  targets: ModelTarget[];
  /** Provider connections, to name the connection of each model in the Routing Intelligence tiers (optional). */
  connections?: { id: string; name: string }[];
  busy: boolean;
  onSave: (body: EditableRoutingBody) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const [body, setBody] = useState<EditableRoutingBody>(initial);
  const strong = targets.filter((x) => x.strong);
  const setRole = (role: AgentRole, route: RoleRoute | null) =>
    setBody((b) => {
      const roles = { ...b.roles };
      if (route) roles[role] = route;
      else delete roles[role];
      return { ...b, roles };
    });

  return (
    <form
      className="stack routing-editor"
      data-testid="routing-editor"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(body);
      }}
      noValidate
    >
      <FormSection title={t("routing.roles")} description={t("routing.rolesHint")}>
        <div className="table-wrap" style={{ gridColumn: "1 / -1" }}>
          <table className="table">
            <caption className="sr-only">{t("routing.roles")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("agents.role")}</th>
                <th scope="col">{t("routing.defaultAgent")}</th>
                <th scope="col">{t("routing.fallbacksAndCaps")}</th>
              </tr>
            </thead>
            <tbody>
              {ROLE_IDS.filter((r) => r !== "maestro").map((role) => {
                const route = body.roles[role];
                const ofRole = profiles.filter((p) => p.role === role && p.state !== "disabled");
                return (
                  <tr key={role}>
                    <th scope="row">{t(`agentRole.${role}`)}</th>
                    <td>
                      <SelectField
                        label={<span className="sr-only">{t("routing.defaultAgentFor", { role: t(`agentRole.${role}`) })}</span>}
                        value={route?.default_agent_profile_id ?? ""}
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) setRole(role, null);
                          else
                            setRole(role, {
                              agent_profile_ids: Array.from(new Set([id, ...(route?.agent_profile_ids ?? []).filter((x) => ofRole.some((p) => p.id === x))])),
                              default_agent_profile_id: id,
                              required_capabilities: route?.required_capabilities ?? [],
                              fallbacks: route?.fallbacks ?? [],
                            });
                        }}
                      >
                        <option value="">{t("routing.notConfigured")}</option>
                        {ofRole.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {targetName(targets, p.model_target_id) === "—" ? t("agents.noModel") : targetName(targets, p.model_target_id)}
                          </option>
                        ))}
                      </SelectField>
                      {route && ofRole.length > 1 ? (
                        <fieldset className="field" style={{ marginTop: 6 }}>
                          <legend className="field-hint">{t("routing.allowedAgents")}</legend>
                          {ofRole.map((p) => (
                            <label key={p.id} className="choice">
                              <input
                                type="checkbox"
                                checked={route.agent_profile_ids.includes(p.id)}
                                disabled={p.id === route.default_agent_profile_id}
                                onChange={(e) =>
                                  setRole(role, {
                                    ...route,
                                    agent_profile_ids: e.target.checked ? [...route.agent_profile_ids, p.id] : route.agent_profile_ids.filter((x) => x !== p.id),
                                  })
                                }
                              />
                              <span>{p.name}</span>
                            </label>
                          ))}
                        </fieldset>
                      ) : null}
                    </td>
                    <td>{route ? <RouteDetails route={route} targets={targets} onChange={(r) => setRole(role, r)} role={role} /> : <span className="subtle">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </FormSection>

      <FormSection title={t("routing.maestro")} description={t("routing.maestroHint")} testId="maestro-section">
        <SelectField
          label={t("routing.maestroPrimary")}
          value={body.maestro.model_target_id ?? ""}
          onChange={(e) => setBody((b) => ({ ...b, maestro: { ...b.maestro, model_target_id: e.target.value || null } }))}
          hint={strong.length === 0 ? t("routing.noStrongTargets") : undefined}
        >
          <option value="">{t("routing.maestroNone")}</option>
          {targets.map((x) => (
            <option key={x.id} value={x.id}>
              {x.display_name} ({x.external_model_id}){x.strong ? ` — ${t("models.strong")}` : ` — ${t("routing.notStrong")}`}
            </option>
          ))}
        </SelectField>
        <fieldset className="field">
          <legend className="field-label">{t("routing.substitutes")}</legend>
          {strong.length === 0 ? <p className="subtle">{t("routing.noStrongTargets")}</p> : null}
          {strong
            .filter((x) => x.id !== body.maestro.model_target_id)
            .map((x) => (
              <label key={x.id} className="choice">
                <input
                  type="checkbox"
                  checked={body.maestro.substitute_model_target_ids.includes(x.id)}
                  onChange={(e) =>
                    setBody((b) => ({
                      ...b,
                      maestro: {
                        ...b.maestro,
                        substitute_model_target_ids: e.target.checked ? [...b.maestro.substitute_model_target_ids, x.id].slice(0, 5) : b.maestro.substitute_model_target_ids.filter((s) => s !== x.id),
                      },
                    }))
                  }
                />
                <span>
                  {x.display_name} <span className="mono subtle">{x.external_model_id}</span>
                </span>
              </label>
            ))}
        </fieldset>
      </FormSection>

      <FormSection title={t("routing.freeFirst")} description={t("routing.freeFirstHint")}>
        <label className="choice">
          <input type="checkbox" checked={body.free_first.enabled} onChange={(e) => setBody((b) => ({ ...b, free_first: { ...b.free_first, enabled: e.target.checked } }))} />
          <span>{t("routing.freeFirstEnabled")}</span>
        </label>
        <fieldset className="field">
          <legend className="field-label">{t("routing.escalationTriggers")}</legend>
          {ESCALATION_TRIGGERS.map((k) => (
            <label key={k} className="choice">
              <input
                type="checkbox"
                checked={body.free_first.escalation_triggers.includes(k)}
                onChange={(e) =>
                  setBody((b) => ({
                    ...b,
                    free_first: { ...b.free_first, escalation_triggers: e.target.checked ? [...b.free_first.escalation_triggers, k] : b.free_first.escalation_triggers.filter((x) => x !== k) },
                  }))
                }
              />
              <span>{t(`trigger.${k}`)}</span>
            </label>
          ))}
        </fieldset>
      </FormSection>

      <FormSection title={t("routing.budgets")}>
        <TextField
          label={t("routing.softLimit")}
          optionalLabel={t("common.optional")}
          type="number"
          min={0}
          value={body.budgets.soft_limit_cents ?? ""}
          onChange={(e) => setBody((b) => ({ ...b, budgets: { ...b.budgets, soft_limit_cents: numOrNull(e.target.value) } }))}
        />
        <TextField
          label={t("routing.hardLimit")}
          optionalLabel={t("common.optional")}
          type="number"
          min={0}
          value={body.budgets.hard_limit_cents ?? ""}
          onChange={(e) => setBody((b) => ({ ...b, budgets: { ...b.budgets, hard_limit_cents: numOrNull(e.target.value) } }))}
        />
        <SelectField label={t("routing.window")} value={body.budgets.window} onChange={(e) => setBody((b) => ({ ...b, budgets: { ...b.budgets, window: e.target.value as "day" | "week" | "month" } }))}>
          {(["day", "week", "month"] as const).map((w) => (
            <option key={w} value={w}>
              {t(`window.${w}`)}
            </option>
          ))}
        </SelectField>
      </FormSection>

      <GauntletFields value={body.gauntlet} onChange={(g) => setBody((b) => ({ ...b, gauntlet: g }))} />

      <RoutingIntelligenceTierFields
        value={body.routing_intelligence}
        targets={targets}
        connections={connections}
        onChange={(ri) => setBody((b) => ({ ...b, routing_intelligence: ri }))}
      />

      <div className="row row--wrap">
        <Button type="submit" variant="primary" loading={busy}>
          {t("routing.saveDraft")}
        </Button>
        <Button onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <p className="field-hint">{t("routing.saveDraftHint")}</p>
      </div>
    </form>
  );
}

export function GauntletFields({ value, onChange }: { value: RoutingConfigBody["gauntlet"]; onChange: (g: RoutingConfigBody["gauntlet"]) => void }) {
  const { t } = useI18n();
  return (
    <FormSection title={t("routing.gauntlet")} description={t("routing.gauntletHint")} testId="gauntlet-section">
      <SelectField label={t("routing.gauntletMode")} value={value.mode} onChange={(e) => onChange({ ...value, mode: e.target.value as "conventional" | "gauntlet" })}>
        <option value="conventional">{t("gauntletMode.conventional")}</option>
        <option value="gauntlet">{t("gauntletMode.gauntlet")}</option>
      </SelectField>
      <TextField
        label={t("routing.maxRounds")}
        type="number"
        min={1}
        max={20}
        value={value.max_rounds}
        onChange={(e) => onChange({ ...value, max_rounds: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
      />
      <TextField
        label={t("routing.stagnationRounds")}
        hint={t("routing.stagnationHint")}
        type="number"
        min={1}
        max={10}
        value={value.stagnation_rounds}
        onChange={(e) => onChange({ ...value, stagnation_rounds: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })}
      />
      <fieldset className="field">
        <legend className="field-label">{t("routing.gauntletRequiredFor")}</legend>
        {GAUNTLET_REQUIRED.map((k) => (
          <label key={k} className="choice">
            <input
              type="checkbox"
              checked={value.required_for.includes(k)}
              onChange={(e) => onChange({ ...value, required_for: e.target.checked ? [...value.required_for, k] : value.required_for.filter((x) => x !== k) })}
            />
            <span>{t(`gauntletReq.${k}`)}</span>
          </label>
        ))}
      </fieldset>
    </FormSection>
  );
}

type RoutingIntelligenceSection = RoutingConfigBody["routing_intelligence"];
type TierKey = "economy_model_target_ids" | "premium_model_target_ids";

/**
 * D29: the Routing Intelligence switch and its economy/premium model tiers (at most 10 targets each; a target may sit in
 * both). Only chat/decision targets are offered; unverified ones may be chosen but routing skips them until verified.
 */
export function RoutingIntelligenceTierFields({
  value,
  targets,
  connections = [],
  onChange,
}: {
  value: RoutingIntelligenceSection;
  targets: ModelTarget[];
  connections?: { id: string; name: string }[];
  onChange: (ri: RoutingIntelligenceSection) => void;
}) {
  const { t } = useI18n();
  return (
    <FormSection title={t("ri.tiers.title")} description={t("ri.tiers.hint")} testId="ri-tiers-section">
      <label className="choice" style={{ gridColumn: "1 / -1" }}>
        <input type="checkbox" checked={value.enabled} onChange={(e) => onChange({ ...value, enabled: e.target.checked })} data-testid="ri-enabled" aria-describedby="ri-enabled-hint" />
        <span>
          {t("ri.tiers.enabled")}
          <span id="ri-enabled-hint" className="field-hint" style={{ display: "block" }}>
            {t("ri.tiers.enabledHint")}
          </span>
        </span>
      </label>
      <TierList tier="economy" field="economy_model_target_ids" value={value} targets={targets} connections={connections} onChange={onChange} />
      <TierList tier="premium" field="premium_model_target_ids" value={value} targets={targets} connections={connections} onChange={onChange} />
    </FormSection>
  );
}

function TierList({
  tier,
  field,
  value,
  targets,
  connections,
  onChange,
}: {
  tier: "economy" | "premium";
  field: TierKey;
  value: RoutingIntelligenceSection;
  targets: ModelTarget[];
  connections: { id: string; name: string }[];
  onChange: (ri: RoutingIntelligenceSection) => void;
}) {
  const { t } = useI18n();
  const ids = value[field];
  const options = tierOptions(targets, ids);
  const full = ids.length >= RI_TIER_MAX;
  const unverifiedChosen = ids.some((id) => {
    const x = targets.find((m) => m.id === id);
    return !x || x.state !== "verified";
  });
  const connName = (id: string) => connections.find((c) => c.id === id)?.name ?? null;
  return (
    <fieldset className="field" data-testid={`ri-tier-${tier}`}>
      <legend className="field-label">{t(`ri.tiers.${tier}`)}</legend>
      <p className="field-hint">{t(`ri.tiers.${tier}Hint`)}</p>
      <p className="field-hint" data-testid={`ri-tier-${tier}-count`} aria-live="polite">
        {full ? t("ri.tiers.full", { max: RI_TIER_MAX }) : t("ri.tiers.count", { n: ids.length, max: RI_TIER_MAX })}
      </p>
      {options.length === 0 ? <p className="subtle">{t("ri.tiers.noTargets")}</p> : null}
      {options.map(({ id, target }) => {
        const checked = ids.includes(id);
        const conn = target ? connName(target.connection_id) : null;
        return (
          <label key={id} className="choice">
            <input
              type="checkbox"
              checked={checked}
              disabled={!checked && full}
              onChange={(e) => onChange({ ...value, [field]: toggleTierId(ids, id, e.target.checked) })}
              data-testid={`ri-tier-${tier}-${id}`}
            />
            <span>
              {target ? target.display_name : <span className="mono">{id}</span>} {target ? <span className="mono subtle">{target.external_model_id}</span> : null}
              {conn ? <span className="subtle"> · {t("ri.tiers.via", { connection: conn })}</span> : null}
              {!target ? <span className="badge badge--warning"> {t("ri.tiers.unknownTarget")}</span> : target.state !== "verified" ? <span className="badge badge--warning"> {t("ri.tiers.notVerified", { state: t(`state.${target.state}`) })}</span> : null}
              {target && target.kind !== "chat" && target.kind !== "decision" ? <span className="badge badge--warning"> {t(`targetKind.${target.kind}`)}</span> : null}
            </span>
          </label>
        );
      })}
      {unverifiedChosen ? (
        <p className="field-hint tone-warning" data-testid={`ri-tier-${tier}-unverified`}>
          {t("ri.tiers.unverifiedWarning")}
        </p>
      ) : null}
    </fieldset>
  );
}

function RouteDetails({ route, targets, onChange, role }: { route: RoleRoute; targets: ModelTarget[]; onChange: (r: RoleRoute) => void; role: AgentRole }) {
  const { t } = useI18n();
  const [target, setTarget] = useState("");
  const [on, setOn] = useState<string[]>(["rate_limited", "unavailable"]);
  return (
    <details className="disclosure">
      <summary>{t("routing.routeSummary", { fallbacks: route.fallbacks.length, caps: route.required_capabilities.length })}</summary>
      <div className="stack" style={{ gap: 8 }}>
        <fieldset className="field">
          <legend className="field-label">{t("routing.requiredCaps")}</legend>
          <div className="row row--wrap" style={{ gap: 10 }}>
            {CAPABILITY_KEYS.map((k) => (
              <label key={k} className="choice">
                <input
                  type="checkbox"
                  checked={route.required_capabilities.includes(k)}
                  onChange={(e) =>
                    onChange({ ...route, required_capabilities: e.target.checked ? [...route.required_capabilities, k] : route.required_capabilities.filter((x) => x !== k) })
                  }
                />
                <span>{t(`cap.${k}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {route.fallbacks.length > 0 ? (
          <ol className="plain-list">
            {route.fallbacks.map((f, i) => (
              <li key={`${f.model_target_id}-${i}`} className="row row--wrap">
                <span className="badge">{i + 1}</span>
                <span className="mono">{targetName(targets, f.model_target_id)}</span>
                <span className="subtle">{f.on.map((o) => t(`errorClass.${o}`)).join(", ")}</span>
                <Button size="sm" variant="ghost" onClick={() => onChange({ ...route, fallbacks: route.fallbacks.filter((_, n) => n !== i) })}>
                  {t("common.remove")}
                </Button>
              </li>
            ))}
          </ol>
        ) : null}
        <div className="stack" style={{ gap: 6 }}>
          <SelectField label={t("routing.addFallbackFor", { role: t(`agentRole.${role}`) })} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">{t("agents.chooseModel")}</option>
            {targets.map((x) => (
              <option key={x.id} value={x.id}>
                {x.display_name} ({x.external_model_id})
              </option>
            ))}
          </SelectField>
          <fieldset className="field">
            <legend className="field-hint">{t("routing.fallbackOn")}</legend>
            <div className="row row--wrap" style={{ gap: 10 }}>
              {FALLBACK_ERRORS.map((k) => (
                <label key={k} className="choice">
                  <input type="checkbox" checked={on.includes(k)} onChange={(e) => setOn((o) => (e.target.checked ? [...o, k] : o.filter((x) => x !== k)))} />
                  <span>{t(`errorClass.${k}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <Button
              size="sm"
              disabled={!target || on.length === 0}
              onClick={() => {
                onChange({ ...route, fallbacks: [...route.fallbacks, { model_target_id: target, on: on as RoleRoute["fallbacks"][number]["on"] }].slice(0, 10) });
                setTarget("");
              }}
            >
              {t("routing.addFallback")}
            </Button>
          </div>
        </div>
      </div>
    </details>
  );
}

/** Validation summary: ok, or the exact impediments (never a vague "invalid"). */
export function ValidationSummary({ config }: { config: RoutingConfigRecord }) {
  const { t } = useI18n();
  if (!config.validation) return <span className="subtle">{t("routing.notValidated")}</span>;
  if (config.validation.ok)
    return (
      <Pill tone="success" icon="check">
        {t("routing.valid")}
      </Pill>
    );
  return (
    <div className="stack" style={{ gap: 4 }}>
      <Pill tone="warning" icon="alert">
        {t("routing.impedimentCount", { n: config.validation.impediments.length })}
      </Pill>
      <ul className="notes" data-testid="routing-impediments">
        {config.validation.impediments.map((i, n) => (
          <li key={n}>
            <span className="mono">{i.path}</span>: <span className="user-content">{i.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Top-level sections that differ from the previous version (a readable diff between versions). */
export function changedSections(a: RoutingConfigLayer | undefined, b: RoutingConfigLayer | undefined): string[] {
  const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
  const out: string[] = [];
  for (const k of keys) {
    const va = (a as Record<string, unknown> | undefined)?.[k];
    const vb = (b as Record<string, unknown> | undefined)?.[k];
    if (k === "roles" && va && vb && typeof va === "object" && typeof vb === "object") {
      const roles = new Set([...Object.keys(va), ...Object.keys(vb)]);
      for (const r of roles) if (JSON.stringify((va as Record<string, unknown>)[r]) !== JSON.stringify((vb as Record<string, unknown>)[r])) out.push(`roles.${r}`);
    } else if (JSON.stringify(va) !== JSON.stringify(vb)) out.push(k);
  }
  return out;
}

/** Versions of one scope with validate/activate (rollback = activating an earlier version). */
export function RoutingVersionList({
  configs,
  canWrite,
  onChanged,
  showDiff = false,
  filter,
  emptyText,
}: {
  configs: RoutingConfigRecord[];
  canWrite: boolean;
  onChanged: () => void;
  showDiff?: boolean;
  filter?: (c: RoutingConfigRecord) => boolean;
  emptyText: string;
}) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; error: unknown } | null>(null);
  const activeVersion = configs.find((c) => c.state === "active")?.version ?? 0;
  const shown = filter ? configs.filter(filter) : configs;
  const sorted = [...configs].sort((a, b) => a.version - b.version);

  const act = async (c: RoutingConfigRecord, kind: "validate" | "activate") => {
    setBusy(`${c.id}:${kind}`);
    setError(null);
    try {
      if (kind === "validate") await validateRoutingConfig(c.id);
      else {
        await activateRoutingConfig(c.id, activeVersion);
        toast(t("routing.activated", { n: c.version }), "success");
      }
      onChanged();
    } catch (e) {
      setError({ id: c.id, error: e });
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  if (shown.length === 0) return <p className="subtle">{emptyText}</p>;
  return (
    <ul className="config-list" data-testid="routing-versions">
      {shown.map((c) => {
        const prev = sorted.filter((x) => x.version < c.version).at(-1);
        const diff = showDiff ? changedSections(prev?.body, c.body) : [];
        const earlier = c.state !== "active" && c.version < activeVersion;
        return (
          <li key={c.id} className="card card--pad stack" data-testid={`routing-version-${c.version}`}>
            <div className="row row--wrap">
              <strong>{t("routing.versionN", { n: c.version })}</strong>
              <StateBadge state={c.state} />
              <span className="subtle">{formatDateTime(c.created_at)}</span>
              {c.activated_at ? <span className="subtle">· {t("routing.activatedAt", { when: formatDateTime(c.activated_at) })}</span> : null}
              {c.created_by ? <span className="subtle">· {t("routing.author", { who: c.created_by })}</span> : null}
            </div>
            <ValidationSummary config={c} />
            {showDiff ? (
              <p className="field-hint" data-testid="routing-diff">
                {prev ? (diff.length === 0 ? t("routing.noChanges", { n: prev.version }) : t("routing.changedSince", { n: prev.version, list: diff.join(", ") })) : t("routing.firstVersion")}
              </p>
            ) : null}
            {canWrite && c.state !== "disabled" ? (
              <div className="row row--wrap">
                <Button size="sm" onClick={() => void act(c, "validate")} loading={busy === `${c.id}:validate`} disabled={busy !== null}>
                  {t("routing.validate")}
                </Button>
                {c.state !== "active" ? (
                  <Button size="sm" variant={earlier ? "secondary" : "primary"} onClick={() => void act(c, "activate")} loading={busy === `${c.id}:activate`} disabled={busy !== null}>
                    {earlier ? t("routing.rollbackTo", { n: c.version }) : t("routing.activate")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {error?.id === c.id ? <ActionError error={error.error} onDismiss={() => setError(null)} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Effective, inherited table (RF-CFG-06/14) with origin per row and an optional action. */
export function EffectiveTable({ view, targets, action }: { view: EffectiveConfigView; targets?: ModelTarget[]; action?: (row: EffectiveRow) => ReactNode }) {
  const { t } = useI18n();
  const maestroName = targetName(targets ?? [], view.maestro.model_target_id);
  return (
    <div className="stack" data-testid="effective-config">
      <div className="row row--wrap">
        {view.ready ? (
          <Pill tone="success" icon="check">
            {t("effective.ready")}
          </Pill>
        ) : (
          <Pill tone="warning" icon="alert">
            {t("effective.notReady")}
          </Pill>
        )}
        <span className="subtle">{t("effective.versions", { global: view.routing_config_versions.global ?? "—", project: view.routing_config_versions.project ?? "—" })}</span>
      </div>
      {view.impediments.length > 0 ? (
        <ul className="notes" data-testid="effective-impediments">
          {view.impediments.map((i, n) => (
            <li key={n} className="user-content">
              {i}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="table-wrap">
        <table className="table effective-table">
          <caption className="sr-only">{t("effective.caption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("effective.agent")}</th>
              <th scope="col">{t("agents.role")}</th>
              <th scope="col">{t("effective.modelApi")}</th>
              <th scope="col">{t("agents.tools")}</th>
              <th scope="col">{t("providers.quotaPool")}</th>
              <th scope="col">{t("agents.fallbacks")}</th>
              <th scope="col">{t("agents.reviewer")}</th>
              <th scope="col">{t("effective.origin")}</th>
              {action ? (
                <th scope="col">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {view.rows.map((r) => (
              <tr key={r.role} data-testid={`effective-row-${r.role}`} className={r.ready ? undefined : "is-highlighted"}>
                <td>{r.agent_name ?? <span className="subtle">{t("routing.notConfigured")}</span>}</td>
                <td>{t(`agentRole.${r.role}`)}</td>
                <td>
                  <span className="mono">{r.model_display ?? "—"}</span>
                  {r.api ? <span className="subtle" style={{ display: "block" }}>{r.api}</span> : null}
                  {r.impediment ? <span className="tone-warning user-content" style={{ display: "block" }}>{r.impediment}</span> : null}
                </td>
                <td>
                  {r.tools.length > 0 ? r.tools.join(", ") : "—"}
                  {r.declared_tools.length > 0 ? <span className="subtle" style={{ display: "block" }}>{t("effective.toolsDeclared", { list: r.declared_tools.join(", ") })}</span> : null}
                </td>
                <td>{r.quota_pool ?? "—"}</td>
                <td>{r.fallbacks.length > 0 ? r.fallbacks.join(" → ") : "—"}</td>
                <td>{r.reviewer ? t(`reviewer.${r.reviewer as "maestro"}`) : "—"}</td>
                <td>
                  <OriginBadge origin={r.origin} />
                </td>
                {action ? <td>{action(r)}</td> : null}
              </tr>
            ))}
            <tr data-testid="effective-row-maestro-selection">
              <td>{t("routing.maestro")}</td>
              <td>{t("agentRole.maestro")}</td>
              <td colSpan={action ? 7 : 6}>
                <span className="mono">{maestroName}</span>
                {view.maestro.substitutes.length > 0 ? <span className="subtle"> · {t("routing.substitutesList", { list: view.maestro.substitutes.map((s) => targetName(targets ?? [], s)).join(", ") })}</span> : null}
                {view.maestro.impediment ? <span className="tone-warning user-content" style={{ display: "block" }}>{view.maestro.impediment}</span> : null}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
